import type { Metric } from '@/client'

/**
 * The dashboard's headline figures and the colour each keeps wherever it is
 * drawn: its KPI card's swatch and sparkline, and every chart of it once it is
 * picked — over time, its forecast, its growth, its groups, its flow — so the
 * card and the charts below it read as one thing. Anything that is not a KPI
 * (stock, ads lists, tickets) is drawn in the data ink, `DATA_SLOT`.
 *
 * This is the only place a figure is given a colour. A component asks for
 * `seriesVar(metric)` (a CSS variable, for recharts) or `SERIES_BG[slot]` (a
 * class, for a swatch); it never names a `chart-N` of its own.
 */

export const KPI_METRICS = ['sales', 'units', 'ad_spend', 'ad_sales', 'acos', 'clicks'] as const
export type KpiMetric = (typeof KPI_METRICS)[number]

/** A chart-series slot, 1–8: `--chart-N` in `globals.css`. */
export type SeriesSlot = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8

/** The data ink: every single-series chart on the dashboard. */
export const DATA_SLOT: SeriesSlot = 1

export type MetricSpec = {
  slot: SeriesSlot
  /** How its numbers read. */
  kind: 'money' | 'count' | 'ratio'
  /** The report it comes from. */
  source: 'sales' | 'ads'
  /**
   * The dashboard endpoints' name for it, where it has a forecast, a flow and
   * a growth series; units, clicks and ACOS have none.
   */
  dashboard?: Metric
}

export const METRIC_SPECS: Record<KpiMetric, MetricSpec> = {
  sales: { slot: 2, kind: 'money', source: 'sales', dashboard: 'total_sales' },
  units: { slot: 5, kind: 'count', source: 'sales' },
  ad_spend: { slot: 3, kind: 'money', source: 'ads', dashboard: 'ams_spend' },
  ad_sales: { slot: 1, kind: 'money', source: 'ads', dashboard: 'ams_sales' },
  // Six hues far apart in every theme; never the rose slot for ACOS, which
  // would read as an error.
  acos: { slot: 6, kind: 'ratio', source: 'ads' },
  clicks: { slot: 4, kind: 'count', source: 'ads' },
}

/** A slot as the CSS variable recharts paints with, so every theme repaints it. */
export function slotVar(slot: SeriesSlot): string {
  return `var(--chart-${slot})`
}

/** A figure's colour as a CSS variable. */
export function seriesVar(metric: KpiMetric): string {
  return slotVar(METRIC_SPECS[metric].slot)
}

/**
 * A figure's shades: its colour mixed toward the theme's ink (darker) or its
 * card (lighter), by turns — far enough apart to tell side by side, and all
 * of them the figure's own hue, in whichever theme is on.
 */
const SHADE_STEPS = [
  { toward: 'none', keep: 100 },
  { toward: 'ink', keep: 62 },
  { toward: 'card', keep: 55 },
  { toward: 'ink', keep: 40 },
  { toward: 'card', keep: 32 },
  { toward: 'ink', keep: 80 },
  { toward: 'card', keep: 78 },
] as const

/**
 * A palette from a figure's colour: shade `index` of it — the colour itself,
 * then the steps above — so the parts of one chart (a flow's stores) are told
 * apart and still read as that figure. Past the last step, the shades repeat.
 */
export function seriesShade(metric: KpiMetric, index: number): string {
  const step = SHADE_STEPS[index % SHADE_STEPS.length]!
  if (step.toward === 'none') return seriesVar(metric)
  const other = step.toward === 'ink' ? 'var(--foreground)' : 'var(--card)'
  return `color-mix(in oklab, ${seriesVar(metric)} ${step.keep}%, ${other})`
}

/** Swatch fills, written out whole so Tailwind sees every class. Index by slot. */
export const SERIES_BG: Record<SeriesSlot, string> = {
  1: 'bg-chart-1',
  2: 'bg-chart-2',
  3: 'bg-chart-3',
  4: 'bg-chart-4',
  5: 'bg-chart-5',
  6: 'bg-chart-6',
  7: 'bg-chart-7',
  8: 'bg-chart-8',
}

/** A figure's swatch class. */
export function seriesBg(metric: KpiMetric): string {
  return SERIES_BG[METRIC_SPECS[metric].slot]
}

/**
 * Stock age, youngest first: one series hue deepening with age, and the
 * oldest band in the warning ink — stock that old needs a decision. `swatch`
 * repeats `fill` at `opacity` as a class, for a legend.
 */
export const AGE_RAMP = [
  { fill: slotVar(DATA_SLOT), opacity: 0.2, swatch: 'bg-chart-1/20' },
  { fill: slotVar(DATA_SLOT), opacity: 0.35, swatch: 'bg-chart-1/35' },
  { fill: slotVar(DATA_SLOT), opacity: 0.5, swatch: 'bg-chart-1/50' },
  { fill: slotVar(DATA_SLOT), opacity: 0.7, swatch: 'bg-chart-1/70' },
  { fill: slotVar(DATA_SLOT), opacity: 1, swatch: 'bg-chart-1' },
  { fill: 'var(--warning)', opacity: 1, swatch: 'bg-warning' },
] as const
